import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Form,
    Row,
    Col,
    Button,
    Modal,
    Card,
    Alert,
    Spinner,
} from 'react-bootstrap';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './DataTable.css';

const ChequeReporting = () => {
    const baseurl = process.env.REACT_APP_API_URL;
    const [reports, setReports] = useState([]);
    const [isTableLoading, setIsTableLoading] = useState(true);
    const [error, setError] = useState('');
    const [analytics, setAnalytics] = useState({});
    const [selectedReport, setSelectedReport] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [viewMode, setViewMode] = useState('table'); // 'table' | 'cards'

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [city, setCity] = useState('');
    const [rank, setRank] = useState('');
    const [dsCode, setDsCode] = useState('');
    const [mobile, setMobile] = useState('');
    const [starSapphireUpline, setStarSapphireUpline] = useState('');
    const [topazUpline, setTopazUpline] = useState('');

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const itemsPerPage = 50;

    const fetchReports = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setError('No authentication token found. Please log in again.');
            setIsTableLoading(false);
            return;
        }

        setIsTableLoading(true);
        setError('');
        try {
            const params = {
                per_page: itemsPerPage,
                page: currentPage,
                name: searchTerm || undefined,
                city: city || undefined,
                rank: rank || undefined,
                ds_code: dsCode || undefined,
                mobile: mobile || undefined,
                star_sapphire_upline: starSapphireUpline || undefined,
                topaz_upline: topazUpline || undefined,
            };

            Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

            const response = await axios.get(`${baseurl}/cheque-reporting`, {
                params,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            setReports(response.data.data || []);
            setAnalytics(response.data.analytics || {});
            setTotalPages(response.data.pagination?.last_page || 1);
            setTotalItems(response.data.pagination?.total || 0);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch cheque reports');
        } finally {
            setIsTableLoading(false);
        }
    }, [baseurl, currentPage, searchTerm, city, rank, dsCode, mobile, starSapphireUpline, topazUpline]);

    useEffect(() => {
        fetchReports();
    }, [currentPage, fetchReports]);

    const handleRowClick = (report) => {
        setSelectedReport(report);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedReport(null);
    };

    // Export to CSV
    const exportToCSV = () => {
        const headers = ['Name', 'City', 'Rank', 'DS Code', 'Mobile', 'Weak Cheque', 'Next Weak Commitment', 'Star Sapphire Upline', 'Topaz Upline', 'Date Time'];
        const csvContent = [
            headers.join(','),
            ...reports.map(report => [
                `"${report.name || ''}"`,
                `"${report.city || ''}"`,
                `"${report.rank || ''}"`,
                `"${report.ds_code || ''}"`,
                `"${report.mobile || ''}"`,
                `"${report.weak_cheque || ''}"`,
                `"${report.next_weak_comitment || ''}"`,
                `"${report.star_sapphire_upline || ''}"`,
                `"${report.topaz_upline || ''}"`,
                `"${report.date_time ? new Date(report.date_time).toLocaleDateString() : ''}"`,
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `cheque-reports-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Export to PDF
    const exportToPDF = () => {
        try {
            if (reports.length === 0) {
                alert('No data available to export');
                return;
            }

            const doc = new jsPDF('landscape');
            doc.setFontSize(16);
            doc.text('AWPL Cheque Collection Reports', 20, 20);

            const tableColumn = ['Name', 'City', 'Rank', 'DS Code', 'Mobile', 'Weak Cheque', 'Next Weak Commitment', 'Star Sapphire Upline', 'Topaz Upline', 'Date Time'];
            const tableRows = reports.map(report => [
                report.name || '',
                report.city || '',
                report.rank || '',
                report.ds_code || '',
                report.mobile || '',
                report.weak_cheque || '',
                report.next_weak_comitment || '',
                report.star_sapphire_upline || '',
                report.topaz_upline || '',
                report.date_time ? new Date(report.date_time).toLocaleDateString() : '',
            ]);

            autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                startY: 30,
                theme: 'striped',
                headStyles: { fillColor: [16, 185, 129], textColor: [255, 255, 255] },
                styles: { fontSize: 8, cellPadding: 2 },
            });

            doc.save(`cheque-reports-${new Date().toISOString().split('T')[0]}.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again.');
        }
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setCity('');
        setRank('');
        setDsCode('');
        setMobile('');
        setStarSapphireUpline('');
        setTopazUpline('');
        setCurrentPage(1);
    };

    const handleApplyFilters = () => {
        setCurrentPage(1);
        fetchReports();
    };

    return (
        <Container fluid className="reporting-container">
            {/* Header Box */}
            <div className="reporting-header-box">
                <div className="reporting-title-wrap">
                    <h2 className="reporting-title">
                        <span>💳</span> Cheque Collection Reporting
                    </h2>
                    <p className="reporting-subtitle">Manage cheque disbursements, commitments, and upline verifications</p>
                </div>
                <div className="reporting-actions-wrap">
                    <button className="btn-export-pdf" onClick={exportToPDF}>
                        <span>📄</span> Export PDF
                    </button>
                    <button className="btn-print-data" onClick={exportToCSV}>
                        <span>📊</span> Export CSV
                    </button>
                </div>
            </div>

            {/* Analytics KPI Metrics */}
            <div className="analytics-metrics-grid">
                <div className="analytics-stat-card">
                    <div className="analytics-stat-info">
                        <span className="analytics-stat-label">Today's Cheques</span>
                        <span className="analytics-stat-num">{analytics.total_today_entries || 0}</span>
                    </div>
                    <div className="analytics-stat-icon">⚡</div>
                </div>

                <div className="analytics-stat-card">
                    <div className="analytics-stat-info">
                        <span className="analytics-stat-label">Weekly Collections</span>
                        <span className="analytics-stat-num">{analytics.total_week_entries || 0}</span>
                    </div>
                    <div className="analytics-stat-icon">📅</div>
                </div>

                <div className="analytics-stat-card">
                    <div className="analytics-stat-info">
                        <span className="analytics-stat-label">Monthly Volume</span>
                        <span className="analytics-stat-num">{analytics.total_month_entries || 0}</span>
                    </div>
                    <div className="analytics-stat-icon">📈</div>
                </div>

                <div className="analytics-stat-card">
                    <div className="analytics-stat-info">
                        <span className="analytics-stat-label">Total Verified</span>
                        <span className="analytics-stat-num">{analytics.total_no_of_entries || 0}</span>
                    </div>
                    <div className="analytics-stat-icon">🏆</div>
                </div>
            </div>

            {error && (
                <Alert variant="danger" className="mb-4">
                    {error}
                </Alert>
            )}

            {/* Filters Card */}
            <div className="filter-panel-card">
                <div className="filter-card-title">
                    <span>🔍</span> Filter Cheque Records
                </div>
                <div className="filter-grid">
                    <div className="filter-field">
                        <label className="filter-label">Leader Name</label>
                        <Form.Control
                            type="text"
                            placeholder="Search by name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="filter-field">
                        <label className="filter-label">City</label>
                        <Form.Control
                            type="text"
                            placeholder="Filter by city..."
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                        />
                    </div>

                    <div className="filter-field">
                        <label className="filter-label">Rank</label>
                        <Form.Control
                            type="text"
                            placeholder="Filter by rank..."
                            value={rank}
                            onChange={(e) => setRank(e.target.value)}
                        />
                    </div>

                    <div className="filter-field">
                        <label className="filter-label">DS Code</label>
                        <Form.Control
                            type="text"
                            placeholder="Filter DS code..."
                            value={dsCode}
                            onChange={(e) => setDsCode(e.target.value)}
                        />
                    </div>

                    <div className="filter-field">
                        <label className="filter-label">Mobile</label>
                        <Form.Control
                            type="text"
                            placeholder="Filter mobile..."
                            value={mobile}
                            onChange={(e) => setMobile(e.target.value)}
                        />
                    </div>

                    <div className="filter-field">
                        <label className="filter-label">Star Sapphire Upline</label>
                        <Form.Control
                            type="text"
                            placeholder="Filter upline..."
                            value={starSapphireUpline}
                            onChange={(e) => setStarSapphireUpline(e.target.value)}
                        />
                    </div>

                    <div className="filter-field">
                        <label className="filter-label">Topaz Upline</label>
                        <Form.Control
                            type="text"
                            placeholder="Filter topaz upline..."
                            value={topazUpline}
                            onChange={(e) => setTopazUpline(e.target.value)}
                        />
                    </div>

                    <div className="filter-actions">
                        <button className="btn-filter-apply" onClick={handleApplyFilters}>
                            <span>🔍</span> Search
                        </button>
                        <button className="btn-filter-reset" onClick={handleClearFilters}>
                            <span>↺</span> Reset
                        </button>
                    </div>
                </div>
            </div>

            {/* Table / Results Container */}
            <div className="table-data-card">
                {/* Table Toolbar & View Switcher */}
                <div className="table-toolbar-box">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="fw-bold small text-muted">View Mode:</span>
                        <div className="view-mode-toggle-group">
                            <button
                                className={`view-mode-btn ${viewMode === 'table' ? 'active' : ''}`}
                                onClick={() => setViewMode('table')}
                            >
                                <span>📊</span> Table View
                            </button>
                            <button
                                className={`view-mode-btn ${viewMode === 'cards' ? 'active' : ''}`}
                                onClick={() => setViewMode('cards')}
                            >
                                <span>🗂️</span> Card View
                            </button>
                        </div>
                    </div>
                    {viewMode === 'table' && (
                        <div className="mobile-swipe-hint d-md-none">
                            <span>👉</span> Swipe horizontally to view all columns
                        </div>
                    )}
                </div>

                {isTableLoading ? (
                    <div className="d-flex flex-column justify-content-center align-items-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <span className="mt-3 text-muted">Retrieving cheque reports...</span>
                    </div>
                ) : (
                    <>
                        {/* Table View (Works on Desktop & Mobile with Horizontal Scroll) */}
                        {viewMode === 'table' && (
                            <div className="table-responsive-wrapper">
                                <table className="modern-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>City</th>
                                            <th>Rank</th>
                                            <th>DS Code</th>
                                            <th>Mobile</th>
                                            <th>Weak Cheque</th>
                                            <th>Next Commitment</th>
                                            <th>Star Sapphire Upline</th>
                                            <th>Topaz Above Upline</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reports.length > 0 ? (
                                            reports.map((report) => (
                                                <tr
                                                    key={report.id}
                                                    className="table-row-interactive"
                                                    onClick={() => handleRowClick(report)}
                                                >
                                                    <td>
                                                        <div className="user-cell">
                                                            <div className="user-avatar-pill">
                                                                {report.name ? report.name.charAt(0).toUpperCase() : 'C'}
                                                            </div>
                                                            <div className="user-cell-meta">
                                                                <span className="user-name-bold">{report.name || 'Unnamed'}</span>
                                                                <span className="user-sub-id">{report.city || '-'}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{report.city || '-'}</td>
                                                    <td>
                                                        <span className="badge-rank-pill">{report.rank || '-'}</span>
                                                    </td>
                                                    <td>
                                                        <span className="user-sub-id">{report.ds_code || '-'}</span>
                                                    </td>
                                                    <td>{report.mobile || '-'}</td>
                                                    <td>
                                                        <span className="badge-amount-pill">{report.weak_cheque || '-'}</span>
                                                    </td>
                                                    <td>{report.next_weak_comitment || '-'}</td>
                                                    <td>{report.star_sapphire_upline || '-'}</td>
                                                    <td>{report.topaz_upline || '-'}</td>
                                                    <td>
                                                        <button
                                                            className="btn-view-details"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRowClick(report);
                                                            }}
                                                        >
                                                            Details
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="10">
                                                    <div className="empty-table-state">
                                                        <div className="empty-icon">💳</div>
                                                        <div className="empty-title">No Cheque Reports Found</div>
                                                        <div className="empty-desc">No records matched your search parameters.</div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Card View */}
                        {viewMode === 'cards' && (
                            <div className="p-3">
                                {reports.length > 0 ? (
                                    <Row className="g-3">
                                        {reports.map((report) => (
                                            <Col key={report.id} xs={12} md={6} xl={4}>
                                                <Card
                                                    className="h-100 shadow-sm border"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleRowClick(report)}
                                                >
                                                    <Card.Body className="p-3">
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <div>
                                                                <h6 className="fw-bold mb-0 text-primary">{report.name || 'Unnamed'}</h6>
                                                                <small className="text-muted">{report.city || '-'} • DS: {report.ds_code || '-'}</small>
                                                            </div>
                                                            <span className="badge-rank-pill">{report.rank || '-'}</span>
                                                        </div>
                                                        <hr className="my-2 opacity-25" />
                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <span className="small text-secondary"><strong>Weak Cheque:</strong></span>
                                                            <span className="badge-amount-pill">{report.weak_cheque || '-'}</span>
                                                        </div>
                                                        <div className="small text-secondary mb-1">
                                                            <strong>Next Commitment:</strong> {report.next_weak_comitment || '-'}
                                                        </div>
                                                        <div className="small text-secondary mb-1">
                                                            <strong>Star Sapphire:</strong> {report.star_sapphire_upline || '-'}
                                                        </div>
                                                        <div className="small text-secondary mb-2">
                                                            <strong>Mobile:</strong> {report.mobile || '-'}
                                                        </div>
                                                        <Button size="sm" variant="outline-primary" className="w-100 mt-2">
                                                            View Full Details
                                                        </Button>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                ) : (
                                    <div className="empty-table-state">
                                        <div className="empty-icon">💳</div>
                                        <div className="empty-title">No Cheque Reports Found</div>
                                        <div className="empty-desc">Try adjusting your filters.</div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Pagination Bar */}
                        <div className="pagination-container-box">
                            <span className="pagination-summary-text">
                                Showing {reports.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} to{' '}
                                {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} total records
                            </span>
                            <div className="pagination-controls">
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage <= 1}
                                    onClick={() => setCurrentPage(1)}
                                    title="First Page"
                                >
                                    «
                                </button>
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage <= 1}
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    title="Previous Page"
                                >
                                    ‹
                                </button>
                                <span className="px-2 fw-bold text-muted small">
                                    Page {currentPage} of {totalPages || 1}
                                </span>
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                    title="Next Page"
                                >
                                    ›
                                </button>
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage >= totalPages}
                                    onClick={() => setCurrentPage(totalPages)}
                                    title="Last Page"
                                >
                                    »
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Detail Modal */}
            <Modal
                show={showModal}
                onHide={handleCloseModal}
                size="lg"
                centered
                className="detail-modal-custom"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <span>💳</span> Cheque Report Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedReport && (
                        <div className="modal-details-grid">
                            <div className="detail-field-box">
                                <span className="detail-field-label">Leader Name</span>
                                <span className="detail-field-val">{selectedReport.name || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">City</span>
                                <span className="detail-field-val">{selectedReport.city || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Rank</span>
                                <span className="detail-field-val">{selectedReport.rank || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">DS Code</span>
                                <span className="detail-field-val">{selectedReport.ds_code || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Mobile</span>
                                <span className="detail-field-val">{selectedReport.mobile || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Weak Cheque Amount</span>
                                <span className="detail-field-val">{selectedReport.weak_cheque || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Next Weak Commitment</span>
                                <span className="detail-field-val">{selectedReport.next_weak_comitment || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Star Sapphire Upline</span>
                                <span className="detail-field-val">{selectedReport.star_sapphire_upline || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Topaz Above Upline</span>
                                <span className="detail-field-val">{selectedReport.topaz_upline || '-'}</span>
                            </div>
                            <div className="detail-field-box">
                                <span className="detail-field-label">Recorded Date Time</span>
                                <span className="detail-field-val">{selectedReport.date_time || '-'}</span>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ChequeReporting;